import React, { useState, useEffect } from "react";
import { Button, Space, Modal, Form, Input, Switch, Tooltip, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, ExclamationCircleOutlined, GlobalOutlined } from "@ant-design/icons";

// Types
import { PatientResourceLink } from "../../../types/file/ResourceLinkTypes";

// API
import { 
	getGlobalResourceLinks,
	createPatientResourceLink,
	updatePatientResourceLink,
	deletePatientResourceLink 
} from "../../../api/file/patientResourceLinksApi";

import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";

const { TextArea } = Input;
const { confirm } = Modal;

interface GlobalResourceLinksManagementProps {
	companyId: number;
}

interface GlobalResourceForm {
	resource_type: string;
	link_url: string;
	description: string;
	is_active: boolean;
}

const GlobalResourceLinksManagement: React.FC<GlobalResourceLinksManagementProps> = ({ companyId }) => {
	const { showSuccess, showError } = useNotifications();
	const [globalResources, setGlobalResources] = useState<PatientResourceLink[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [editingResource, setEditingResource] = useState<PatientResourceLink | null>(null);
	const [saving, setSaving] = useState<boolean>(false);
	const [form] = Form.useForm();

	useEffect(() => {
		fetchGlobalResources();
	}, [companyId]);

	const fetchGlobalResources = async () => {
		setLoading(true);
		try {
			const response = await getGlobalResourceLinks();
			setGlobalResources(response || []);
		} catch (error) {
			console.error("Error fetching global resources:", error);
			showError("Failed to load global resource links");
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = () => {
		setEditingResource(null);
		form.resetFields();
		form.setFieldsValue({ is_active: true });
		setModalVisible(true);
	};

	const handleEditResource = (resource: PatientResourceLink) => {
		if (resource.admin_template_id !== null) return;
		setEditingResource(resource);
		form.setFieldsValue({
			resource_type: resource.resource_type,
			link_url: resource.link_url,
			description: resource.description,
			is_active: resource.is_active,
		});
		setModalVisible(true);
	};

	const handleCloseModal = () => {
		setModalVisible(false);
		setEditingResource(null);
		form.resetFields();
	};

	const handleSubmit = async (values: GlobalResourceForm) => {
		setSaving(true);
		try {
			const resourceData: PatientResourceLink = {
				id: editingResource ? editingResource.id : 0,
				company_id: companyId,
				patient_id: null, // Global resources belong to no patient
				resource_type: values.resource_type,
				link_url: values.link_url,
				description: values.description,
				is_active: values.is_active,
				is_global: true,
				created_at: editingResource ? editingResource.created_at : new Date(),
				updated_at: editingResource ? editingResource.updated_at :  new Date(),
				admin_template_id: null
			};

			if (editingResource) {
				await updatePatientResourceLink(resourceData);
				showSuccess("Global resource updated successfully!");
			} else {
				await createPatientResourceLink(resourceData);
				showSuccess("Global resource created successfully!");
			}

			await fetchGlobalResources();
			handleCloseModal();
		} catch (error) {
			console.error("Error saving global resource:", error);
			// Surface the file service's 422 per-field validation message rather than a generic failure.
			const messages = extractValidationErrors(error);
			showError(messages[0] ?? "Failed to save global resource");
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteResource = (resource: PatientResourceLink) => {
		confirm({
			title: "Delete Global Resource",
			icon: <ExclamationCircleOutlined />,
			content: `Are you sure you want to delete "${resource.resource_type}"? This action cannot be undone.`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deletePatientResourceLink(resource.id);
					showSuccess("Global resource deleted successfully");
					await fetchGlobalResources();
				} catch (error) {
					console.error("Error deleting global resource:", error);
					showError("Failed to delete global resource");
				}
			},
		});
	};

	const handleOpenLink = (url: string) => {
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const columns = [
		{
			title: "Resource Type",
			dataIndex: "resource_type",
			key: "resource_type",
			sorter: (a: PatientResourceLink, b: PatientResourceLink) => 
				a.resource_type.localeCompare(b.resource_type),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
			width: "40%",
		},
		{
			title: "URL",
			dataIndex: "link_url",
			key: "link_url",
			render: (url: string) => (
				<Button 
					type="link" 
					icon={<LinkOutlined />} 
					onClick={() => handleOpenLink(url)}
					style={{ padding: 0 }}
				>
					Open Link
				</Button>
			),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 110,
			render: (is_active: boolean) => (
				<StatusTag status={is_active ? "active" : "inactive"} />
			),
		},
		{
			title: "",
			key: "actions",
			width: 120,
			render: (_: any, record: PatientResourceLink) => {
				const isAdminManaged = record.admin_template_id !== null;
				return (
					<Space size={2}>
						{isAdminManaged && (
							<Tooltip title="Managed by Admin">
								<Tag icon={<GlobalOutlined />} color="blue" style={{ fontSize: 11 }}>Global</Tag>
							</Tooltip>
						)}
						<Tooltip title={isAdminManaged ? "Cannot edit admin-managed resources" : "Edit"}>
							<Button
								type="text"
								size="small"
								icon={<EditOutlined />}
								disabled={isAdminManaged}
								onClick={(e) => { e.stopPropagation(); handleEditResource(record); }}
							/>
						</Tooltip>
						<Tooltip title={isAdminManaged ? "Cannot delete admin-managed resources" : "Delete"}>
							<Button
								type="text"
								danger
								size="small"
								icon={<DeleteOutlined />}
								disabled={isAdminManaged}
								onClick={(e) => { e.stopPropagation(); handleDeleteResource(record); }}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return (
		<SectionCard
			title="Global Resource Links"
			subtitle="Manage global resource links that can be assigned to any patient in your company"
			extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>Add Global Resource</Button>}
			noPadding
		>
			<DataTable
				columns={columns}
				dataSource={globalResources}
				rowKey={(record) => `${record.company_id}-${record.resource_type}`}
				loading={loading}
				pagination={{ pageSize: 10 }}
				emptyTitle="No global resource links found"
				emptyDescription="Create shared resources that can be assigned to patients"
				emptyAction={{ label: "Create Your First Global Resource", onClick: handleOpenModal }}
				bare
			/>

			<Modal
				title={editingResource ? "Edit Global Resource Link" : "Create Global Resource Link"}
				open={modalVisible}
				onCancel={handleCloseModal}
				footer={null}
				width={600}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
				>
					<Form.Item
						name="resource_type"
						label="Resource Name"
						rules={[{ required: true, message: "Please enter a name for the resource" }]}
					>
						<Input placeholder="e.g., Educational Material, Therapath Website, etc." />
					</Form.Item>

					<Form.Item
						name="link_url"
						label="URL"
						rules={[
							{ required: true, message: "Please enter URL" },
							{ type: "url", message: "Please enter a valid URL" }
						]}
					>
						<Input placeholder="https://example.com" />
					</Form.Item>

					<Form.Item
						name="description"
						label="Description"
						rules={[{ required: true, message: "Please enter description" }]}
					>
						<TextArea 
							rows={3} 
							placeholder="Brief description of the resource that will be visible to patients" 
						/>
					</Form.Item>

					<Form.Item
						name="is_active"
						label="Active"
						valuePropName="checked"
						extra="Only active resources can be assigned to patients"
					>
						<Switch />
					</Form.Item>

					<Form.Item>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
							<Button onClick={handleCloseModal}>
								Cancel
							</Button>
							<Button type="primary" htmlType="submit" loading={saving}>
								{editingResource ? "Update" : "Create"}
							</Button>
						</div>
					</Form.Item>
				</Form>
			</Modal>
		</SectionCard>
	);
};

export default GlobalResourceLinksManagement;